import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Radar, Radio, ShoppingBag, ShieldCheck, Users, FlaskConical, Loader2, Zap, BarChart3 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/adcp`;
const CHANNELS = ["ctv", "web", "mobile", "audio", "dooh"];
const fmt = (n) => n.toLocaleString();

const SourceBadge = () => (
  <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-full">
    <Zap className="w-3 h-3" /> Mock fallback mode
  </span>
);

export default function AdcpPage() {
  // Products
  const [brief, setBrief] = useState("");
  const [channels, setChannels] = useState([]);
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  // Media buys
  const [budget, setBudget] = useState(10000);
  const [campaignName, setCampaignName] = useState("Affiliate Awareness Q3");
  const [mediaBuys, setMediaBuys] = useState([]);
  const [delivery, setDelivery] = useState(null);
  // Signals
  const [signalQuery, setSignalQuery] = useState("");
  const [signals, setSignals] = useState([]);
  const [platform, setPlatform] = useState("the-trade-desk");
  // Services
  const [safety, setSafety] = useState(null);
  const [reach, setReach] = useState(null);
  const [study, setStudy] = useState(null);

  const searchProducts = async () => {
    setLoadingProducts(true);
    try {
      const r = await axios.post(`${API}/get-products`, { brief, channels });
      setProducts(r.data.products);
    } catch { toast.error("Product discovery failed"); }
    finally { setLoadingProducts(false); }
  };

  const searchSignals = async (q = signalQuery) => {
    try {
      const r = await axios.post(`${API}/get-signals`, { query: q, max_results: 20 });
      setSignals(r.data.signals);
    } catch { toast.error("Signal discovery failed"); }
  };

  const loadMediaBuys = () => axios.get(`${API}/media-buys`).then((r) => setMediaBuys(r.data.media_buys)).catch(() => {});

  useEffect(() => { searchProducts(); searchSignals(""); loadMediaBuys(); /* eslint-disable-next-line */ }, []);

  const toggleProduct = (pid) =>
    setSelected((prev) => (prev.includes(pid) ? prev.filter((x) => x !== pid) : [...prev, pid]));

  const createMediaBuy = async () => {
    if (!selected.length) { toast.error("Select at least one product"); return; }
    try {
      const r = await axios.post(`${API}/create-media-buy`, { product_ids: selected, total_budget: budget, campaign_name: campaignName });
      toast.success(`Media buy ${r.data.media_buy.media_buy_id} created`);
      setSelected([]);
      loadMediaBuys();
    } catch { toast.error("Failed to create media buy"); }
  };

  const getDelivery = async (id) => {
    try {
      const r = await axios.get(`${API}/media-buys/${id}/delivery`);
      setDelivery(r.data);
    } catch { toast.error("Failed to fetch delivery"); }
  };

  const activateSignal = async (sig) => {
    try {
      const r = await axios.post(`${API}/activate-signal`, { signal_id: sig.signal_id, platform });
      toast.success(`${sig.name} deployed to ${platform} (${r.data.activation.activation_id})`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Activation failed");
    }
  };

  const verifySafety = async () => {
    if (!selected.length && !products.length) return;
    const ids = selected.length ? selected : products.slice(0, 3).map((p) => p.product_id);
    try {
      const r = await axios.post(`${API}/verify-brand-safety`, { property_ids: ids });
      setSafety(r.data.results);
    } catch { toast.error("Brand safety check failed"); }
  };

  const resolveReach = async () => {
    const ids = signals.slice(0, 3).map((s) => s.signal_id);
    if (!ids.length) { toast.error("Discover signals first"); return; }
    try {
      const r = await axios.post(`${API}/resolve-audience-reach`, { signal_ids: ids, geo: "US" });
      setReach(r.data);
    } catch { toast.error("Reach resolution failed"); }
  };

  const configureStudy = async () => {
    if (!mediaBuys.length) { toast.error("Create a media buy first"); return; }
    try {
      const r = await axios.post(`${API}/configure-brand-lift-study`, { media_buy_id: mediaBuys[0].media_buy_id, metrics: ["awareness", "consideration"] });
      setStudy(r.data.study);
    } catch { toast.error("Study configuration failed"); }
  };

  return (
    <main data-testid="adcp-page" className="max-w-7xl mx-auto px-4 sm:px-8 py-12">
      <div className="flex flex-wrap items-center gap-3 mb-2">
        <p className="uppercase text-xs tracking-[0.2em] font-semibold text-slate-500">AdCP Tools Console</p>
        <SourceBadge />
      </div>
      <h1 className="font-heading text-4xl md:text-5xl tracking-tight mb-4">Agentic advertising sandbox.</h1>
      <p className="text-slate-600 max-w-2xl mb-12 leading-relaxed">
        An interactive implementation of the Ad Context Protocol (AdCP) tool suite — discover inventory, activate audience signals, create media buys and run measurement, all against mock fallback data.
      </p>

      <Tabs defaultValue="products">
        <TabsList className="rounded-full h-auto p-1.5 mb-10 flex-wrap">
          <TabsTrigger value="products" data-testid="adcp-tab-products" className="rounded-full px-5 py-2 gap-2"><Radar className="w-4 h-4" /> Products</TabsTrigger>
          <TabsTrigger value="mediabuys" data-testid="adcp-tab-mediabuys" className="rounded-full px-5 py-2 gap-2"><ShoppingBag className="w-4 h-4" /> Media Buys</TabsTrigger>
          <TabsTrigger value="signals" data-testid="adcp-tab-signals" className="rounded-full px-5 py-2 gap-2"><Radio className="w-4 h-4" /> Signals</TabsTrigger>
          <TabsTrigger value="services" data-testid="adcp-tab-services" className="rounded-full px-5 py-2 gap-2"><ShieldCheck className="w-4 h-4" /> MCP Services</TabsTrigger>
        </TabsList>

        {/* Products */}
        <TabsContent value="products">
          <div className="flex flex-col md:flex-row gap-3 mb-8">
            <input value={brief} onChange={(e) => setBrief(e.target.value)}
              placeholder="Campaign brief, e.g. 'sports streaming' or 'finance'…" data-testid="adcp-brief-input"
              className="flex-1 px-5 py-3 rounded-full bg-white border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
            <div className="flex gap-2 flex-wrap">
              {CHANNELS.map((c) => (
                <button key={c} data-testid={`adcp-channel-${c}`}
                  onClick={() => setChannels((prev) => prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c])}
                  className={`px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider border transition-colors duration-200 ${
                    channels.includes(c) ? "bg-slate-900 border-slate-900 text-white" : "border-slate-300 text-slate-600 hover:border-slate-900"
                  }`}>{c}</button>
              ))}
            </div>
            <button onClick={searchProducts} data-testid="adcp-get-products-button"
              className="flex items-center justify-center gap-2 px-8 py-3 rounded-full bg-emerald-600 text-white font-medium hover:bg-emerald-500 transition-colors duration-200">
              {loadingProducts ? <Loader2 className="w-4 h-4 animate-spin" /> : <Radar className="w-4 h-4" />} get_products
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
            {products.map((p) => (
              <button key={p.product_id} onClick={() => toggleProduct(p.product_id)} data-testid={`adcp-product-${p.product_id}`}
                className={`text-left card-lift bg-white border rounded-lg p-6 ${selected.includes(p.product_id) ? "border-emerald-500 ring-2 ring-emerald-200" : "border-slate-200"}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-semibold uppercase tracking-wider bg-slate-100 px-2 py-0.5 rounded-full">{p.channel}</span>
                  <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${p.brand_safety_tier === "premium" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{p.brand_safety_tier}</span>
                </div>
                <p className="font-heading font-semibold">{p.name}</p>
                <p className="text-xs text-slate-500 mb-3">{p.publisher}</p>
                <p className="text-xs text-slate-600 leading-relaxed mb-4 line-clamp-2">{p.description}</p>
                <div className="flex justify-between text-xs border-t border-slate-100 pt-3">
                  <span className="font-semibold text-emerald-700">${p.cpm} CPM</span>
                  <span className="text-slate-500">{fmt(p.avg_daily_impressions)} imp/day</span>
                  <span className="text-slate-500">min ${fmt(p.min_spend)}</span>
                </div>
              </button>
            ))}
          </div>
          {products.length === 0 && !loadingProducts && <p data-testid="adcp-products-empty" className="text-slate-400 text-sm py-8">No inventory matches this brief.</p>}

          <div className="bg-slate-900 rounded-xl p-8 flex flex-col md:flex-row md:items-end gap-5">
            <div className="flex-1">
              <p className="uppercase text-xs tracking-[0.2em] font-semibold text-emerald-400 mb-1">create_media_buy</p>
              <p className="text-slate-400 text-sm mb-4">{selected.length} product{selected.length !== 1 && "s"} selected</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input value={campaignName} onChange={(e) => setCampaignName(e.target.value)} data-testid="adcp-campaign-name-input"
                  placeholder="Campaign name" className="px-4 py-2.5 rounded-lg bg-white/10 border border-white/20 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                <input type="number" min="1000" value={budget} onChange={(e) => setBudget(parseFloat(e.target.value) || 0)} data-testid="adcp-budget-input"
                  className="px-4 py-2.5 rounded-lg bg-white/10 border border-white/20 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              </div>
            </div>
            <button onClick={createMediaBuy} data-testid="adcp-create-media-buy-button"
              className="px-8 py-3 rounded-full bg-emerald-600 text-white font-medium hover:bg-emerald-500 transition-colors duration-200">
              Create media buy
            </button>
          </div>
        </TabsContent>

        {/* Media buys */}
        <TabsContent value="mediabuys">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-4">
              {mediaBuys.length === 0 && <p data-testid="adcp-mediabuys-empty" className="text-slate-400 text-sm py-8">No media buys yet — create one from the Products tab.</p>}
              {mediaBuys.map((b) => (
                <div key={b.media_buy_id} data-testid={`adcp-mediabuy-${b.media_buy_id}`} className="bg-white border border-slate-200 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-1">
                    <p className="font-heading font-semibold">{b.campaign_name}</p>
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2.5 py-0.5 rounded-full">{b.status}</span>
                  </div>
                  <p className="text-xs text-slate-400 font-mono mb-3">{b.media_buy_id}</p>
                  <p className="text-sm text-slate-600 mb-1">Budget: <span className="font-semibold">${fmt(b.total_budget)}</span> · {b.packages.length} package{b.packages.length !== 1 && "s"}</p>
                  <p className="text-xs text-slate-500 mb-4">{b.packages.map((p) => p.name).join(" · ")}</p>
                  <button onClick={() => getDelivery(b.media_buy_id)} data-testid={`adcp-delivery-${b.media_buy_id}`}
                    className="flex items-center gap-2 text-sm font-medium text-emerald-700 hover:text-emerald-800 transition-colors duration-200">
                    <BarChart3 className="w-4 h-4" /> get_media_buy_delivery
                  </button>
                </div>
              ))}
            </div>
            {delivery && (
              <div data-testid="adcp-delivery-panel" className="bg-slate-900 rounded-xl p-8 h-fit">
                <p className="uppercase text-xs tracking-[0.2em] font-semibold text-emerald-400 mb-1">Delivery report</p>
                <p className="text-xs text-slate-500 font-mono mb-6">{delivery.media_buy_id}</p>
                <div className="grid grid-cols-2 gap-6">
                  {[
                    ["Spend", `$${fmt(delivery.delivery.spend)}`],
                    ["Pacing", `${delivery.delivery.pacing}%`],
                    ["Impressions", fmt(delivery.delivery.impressions)],
                    ["Clicks", fmt(delivery.delivery.clicks)],
                    ["CTR", `${delivery.delivery.ctr}%`],
                    ["VCR", `${delivery.delivery.video_completion_rate}%`],
                    ["eCPM", `$${delivery.delivery.effective_cpm}`],
                    ["Budget", `$${fmt(delivery.delivery.budget)}`],
                  ].map(([l, v]) => (
                    <div key={l}>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">{l}</p>
                      <p className="font-heading text-2xl text-white font-medium">{v}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Signals */}
        <TabsContent value="signals">
          <div className="flex flex-col md:flex-row gap-3 mb-8">
            <input value={signalQuery} onChange={(e) => setSignalQuery(e.target.value)}
              placeholder="Query signals, e.g. 'saas buyers' or 'finance'…" data-testid="adcp-signal-query-input"
              className="flex-1 px-5 py-3 rounded-full bg-white border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
            <Select value={platform} onValueChange={setPlatform}>
              <SelectTrigger data-testid="adcp-platform-select" className="md:w-52 rounded-full bg-white py-3 h-auto"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="the-trade-desk">The Trade Desk</SelectItem>
                <SelectItem value="dv360">DV360</SelectItem>
                <SelectItem value="amazon-dsp">Amazon DSP</SelectItem>
                <SelectItem value="xandr">Xandr</SelectItem>
                <SelectItem value="meta">Meta</SelectItem>
              </SelectContent>
            </Select>
            <button onClick={() => searchSignals()} data-testid="adcp-get-signals-button"
              className="flex items-center justify-center gap-2 px-8 py-3 rounded-full bg-emerald-600 text-white font-medium hover:bg-emerald-500 transition-colors duration-200">
              <Radio className="w-4 h-4" /> get_signals
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {signals.map((s) => (
              <div key={s.signal_id} data-testid={`adcp-signal-${s.signal_id}`} className="bg-white border border-slate-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${s.type === "audience" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{s.type}</span>
                  <span className="text-xs text-slate-400">{s.provider}</span>
                </div>
                <p className="font-heading font-semibold mb-1">{s.name}</p>
                <p className="text-xs text-slate-600 leading-relaxed mb-4">{s.description}</p>
                <div className="flex items-center justify-between border-t border-slate-100 pt-3">
                  <div className="text-xs text-slate-500"><span className="font-semibold text-slate-900">{fmt(s.reach)}</span> reach · +${s.cpm_uplift} CPM</div>
                  <button onClick={() => activateSignal(s)} data-testid={`adcp-activate-${s.signal_id}`}
                    className="flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-slate-900 text-white text-xs font-medium hover:bg-slate-700 transition-colors duration-200">
                    <Zap className="w-3 h-3" /> activate_signal
                  </button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Services */}
        <TabsContent value="services">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="bg-white border border-slate-200 rounded-lg p-8">
              <ShieldCheck className="w-7 h-7 text-emerald-600 mb-3" />
              <h2 className="font-heading text-lg font-semibold mb-2">verify_brand_safety</h2>
              <p className="text-xs text-slate-600 leading-relaxed mb-5">Runs GARM floor, ads.txt and MFA-risk checks on discovered or selected inventory.</p>
              <button onClick={verifySafety} data-testid="adcp-verify-safety-button"
                className="px-6 py-2.5 rounded-full bg-slate-900 text-white text-sm font-medium hover:bg-slate-700 transition-colors duration-200">Run verification</button>
              {safety && (
                <ul data-testid="adcp-safety-results" className="mt-5 space-y-2">
                  {safety.map((r) => (
                    <li key={r.property_id} className="flex items-center justify-between text-xs bg-slate-50 rounded-lg px-3 py-2">
                      <span className="font-mono">{r.property_id}</span>
                      <span className={`font-semibold uppercase ${r.verdict === "pass" ? "text-emerald-700" : "text-amber-600"}`}>{r.verdict}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-8">
              <Users className="w-7 h-7 text-emerald-600 mb-3" />
              <h2 className="font-heading text-lg font-semibold mb-2">resolve_audience_reach</h2>
              <p className="text-xs text-slate-600 leading-relaxed mb-5">Estimates deduplicated, cross-device reach for your first 3 discovered signals (US geo).</p>
              <button onClick={resolveReach} data-testid="adcp-resolve-reach-button"
                className="px-6 py-2.5 rounded-full bg-slate-900 text-white text-sm font-medium hover:bg-slate-700 transition-colors duration-200">Estimate reach</button>
              {reach && (
                <div data-testid="adcp-reach-results" className="mt-5 space-y-2 text-xs">
                  <div className="flex justify-between bg-slate-50 rounded-lg px-3 py-2"><span>Raw reach</span><span className="font-semibold">{fmt(reach.reach.raw_reach)}</span></div>
                  <div className="flex justify-between bg-slate-50 rounded-lg px-3 py-2"><span>Deduplicated</span><span className="font-semibold">{fmt(reach.reach.deduplicated_reach)}</span></div>
                  <div className="flex justify-between bg-emerald-50 rounded-lg px-3 py-2"><span>Addressable devices</span><span className="font-semibold text-emerald-700">{fmt(reach.reach.addressable_devices)}</span></div>
                </div>
              )}
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-8">
              <FlaskConical className="w-7 h-7 text-emerald-600 mb-3" />
              <h2 className="font-heading text-lg font-semibold mb-2">configure_brand_lift_study</h2>
              <p className="text-xs text-slate-600 leading-relaxed mb-5">Configures an awareness + consideration lift study against your most recent media buy.</p>
              <button onClick={configureStudy} data-testid="adcp-configure-study-button"
                className="px-6 py-2.5 rounded-full bg-slate-900 text-white text-sm font-medium hover:bg-slate-700 transition-colors duration-200">Configure study</button>
              {study && (
                <div data-testid="adcp-study-results" className="mt-5 space-y-2 text-xs">
                  <div className="flex justify-between bg-slate-50 rounded-lg px-3 py-2"><span>Study ID</span><span className="font-mono">{study.study_id}</span></div>
                  <div className="flex justify-between bg-slate-50 rounded-lg px-3 py-2"><span>Metrics</span><span className="font-semibold">{study.metrics.join(", ")}</span></div>
                  <div className="flex justify-between bg-slate-50 rounded-lg px-3 py-2"><span>Read date</span><span className="font-semibold">~{study.estimated_read_date_days} days</span></div>
                  <div className="flex justify-between bg-emerald-50 rounded-lg px-3 py-2"><span>Status</span><span className="font-semibold text-emerald-700 uppercase">{study.status}</span></div>
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </main>
  );
}
